import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Introduction */}
        <div className="mb-12 p-6 bg-blue-50 border-l-4 border-blue-600 rounded-r-lg">
          <p className="text-lg text-blue-900 leading-relaxed">
            Your use of MARS's Services is at all times subject to our Terms of Service. 
            Contact <a href="mailto:mars@mediacle.com" className="font-semibold text-blue-700 hover:underline">mars@mediacle.com</a> for alternative formats.
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* Information We Collect */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              Information We Collect
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We collect various types of information to provide and improve our services:
            </p>

            <div className="space-y-8">
              <div className="bg-gray-50 p-8 rounded-lg border">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">1. Personal Information</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Full name, email address, payment details, account credentials, and communications with us.
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg border">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">2. Automatically Collected Information</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Device information, IP address, usage data, and tracking technologies.
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg border">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">3. Third-Party Sources</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Information from social media platforms, analytics partners, and public databases.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              How We Use Your Information
            </h2>

            <div className="grid gap-6">
              <div className="flex items-start p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 mr-6 flex-shrink-0"></div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Services:</h4>
                  <p className="text-lg text-gray-700">Delivering and improving platform functionality</p>
                </div>
              </div>

              <div className="flex items-start p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 mr-6 flex-shrink-0"></div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Personalization:</h4>
                  <p className="text-lg text-gray-700">Customizing content and user experience</p>
                </div>
              </div>

              <div className="flex items-start p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 mr-6 flex-shrink-0"></div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Communication:</h4>
                  <p className="text-lg text-gray-700">Service updates and support</p>
                </div>
              </div>

              <div className="flex items-start p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 mr-6 flex-shrink-0"></div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Security:</h4>
                  <p className="text-lg text-gray-700">Fraud prevention and risk mitigation</p>
                </div>
              </div>

              <div className="flex items-start p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 mr-6 flex-shrink-0"></div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Compliance:</h4>
                  <p className="text-lg text-gray-700">Meeting legal requirements</p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              Data Sharing
            </h2>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8 mb-8">
              <div className="flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-2xl font-bold text-blue-900">We do not sell your personal information.</h3>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We may share data with:
            </p>

            <div className="space-y-6">
              <div className="border-l-4 border-blue-600 bg-blue-50 p-6 rounded-r-lg">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Service Providers:</h4>
                <p className="text-lg text-gray-700">Payment processing, hosting, analytics</p>
              </div>

              <div className="border-l-4 border-blue-600 bg-blue-50 p-6 rounded-r-lg">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Legal Compliance:</h4>
                <p className="text-lg text-gray-700">When required by law or legal proceedings</p>
              </div>

              <div className="border-l-4 border-blue-600 bg-blue-50 p-6 rounded-r-lg">
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Business Transfers:</h4>
                <p className="text-lg text-gray-700">In case of merger or acquisition</p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              Your Rights
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Access:</h4>
                  </div>
                  <p className="text-lg text-gray-700">Request copies of your data</p>
                </div>

                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Rectification:</h4>
                  </div>
                  <p className="text-lg text-gray-700">Correct inaccurate information</p>
                </div>

                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Erasure:</h4>
                  </div>
                  <p className="text-lg text-gray-700">Request deletion of your data</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10 3v6m4-6v6" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Portability:</h4>
                  </div>
                  <p className="text-lg text-gray-700">Obtain and reuse your data</p>
                </div>

                <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L12 12m6.364 6.364L12 12m0 0L5.636 5.636M12 12l6.364-6.364" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Withdraw Consent:</h4>
                  </div>
                  <p className="text-lg text-gray-700">Opt out at any time</p>
                </div>
              </div>
            </div>

         
          </section>

          {/* Contact Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              Contact Information
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-blue-50 border border-blue-200 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <h4 className="text-xl font-semibold text-gray-900">Email:</h4>
                </div>
                <a href="mailto:mars@mediacle.com" className="text-xl text-blue-700 hover:text-blue-900 font-medium hover:underline">
                  mars@mediacle.com
                </a>
              </div>

              <div className="bg-gray-50 border border-gray-200 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h4 className="text-xl font-semibold text-gray-900">Address:</h4>
                </div>
                <div className="text-lg text-gray-700 leading-relaxed">
                  <p>MXG Infotech, 17, 5th Main Rd, Rajagopalnagar, Chamrajpet, Bengaluru, Karnataka 560018</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-300">© 2026 MARS. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;